import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Agents Map · the whole family on one screen.
 * Pulls the LIVE twenty-one-agent roster from the backend (/api/sage/agents/all)
 * and merges with the visible-route + price map for the front door.
 * Falls back to the local hardcoded list only if the fetch fails.
 */

// Local map of visible-route and price info by slug.
// Backend carries the roster shape; the front door adds the human-facing
// walk-through paths and yearly prices.
const ROUTE_MAP = {
  sage:         { title: "Sage",             tagline: "the writing chair",                    price_yr: 100, route: "/sage" },
  baker:        { title: "Baker",            tagline: "the builder",                          price_yr: 200, route: "/baker" },
  quill:        { title: "Quill",            tagline: "the drafting chair",                   price_yr: 200, route: "/quill" },
  loom:         { title: "Loom",             tagline: "the visual weaving chair",             price_yr: 200, route: "/loom" },
  postmaster:   { title: "Postmaster",       tagline: "the social broadcaster",               price_yr: 200, route: "/postmaster" },
  adsmith:      { title: "AdSmith",          tagline: "the campaign engine",                  price_yr: 200, route: "/adsmith" },
  herald:       { title: "Herald",           tagline: "the admin sibling",                    price_yr: 200, route: "/herald" },
  beacon:       { title: "Beacon",           tagline: "public-service signal · FREE forever", price_yr: 0,   route: "/beacon" },
  medic:        { title: "Medic",            tagline: "medical companion · FREE forever",     price_yr: 0,   route: "/medic" },
  legal:        { title: "Legal",            tagline: "plain-english legal helper · FREE forever · with every bundle", price_yr: 0, route: "/legal" },
  cipher:       { title: "Cipher",           tagline: "the cryptographer of the house",       price_yr: 1000, route: null },
  chart:        { title: "Chart",            tagline: "the cartographer",                     price_yr: 1000, route: null },
  anvil:        { title: "Anvil",            tagline: "the smith · batch runner",             price_yr: 1000, route: null },
  scholar:      { title: "Scholar",          tagline: "the librarian",                        price_yr: 1000, route: null },
  bard:         { title: "Bard",             tagline: "the voice-crafter",                    price_yr: 1000, route: null },
  cistern:      { title: "Cistern",          tagline: "the storekeeper · memory vault",       price_yr: 1000, route: null },
  tutor:        { title: "Tutor",            tagline: "the teacher · FREE forever",           price_yr: 0,   route: null },
  bench:        { title: "Bench",            tagline: "the advocate · legal drafter",         price_yr: 200, route: null },
  ledger_agent: { title: "Ledger",           tagline: "the bookkeeper",                       price_yr: 200, route: null },
  compass:      { title: "Compass",          tagline: "the surveyor · FOIA · FREE forever",   price_yr: 0,   route: "/compass" },
  envoy:        { title: "Envoy",            tagline: "the translator",                       price_yr: 200, route: null },
  steward:      { title: "Steward",          tagline: "the grant writer · FREE forever · non-profits", price_yr: 0, route: "/steward" },
  blueprint:    { title: "Blueprint",        tagline: "the trades master · bid drafter",      price_yr: 2000, route: null },
  concierge:    { title: "Concierge",        tagline: "the front-of-house",                   price_yr: 2000, route: null },
  kin:          { title: "Kin",              tagline: "the hearth-keeper · family AI",        price_yr: 2000, route: null },
  science:      { title: "Science",          tagline: "the researcher · free papers · FREE forever", price_yr: 0, route: null },
  almoner:      { title: "Almoner",          tagline: "the charity engine · FREE forever · no AUM skim", price_yr: 0, route: "/almoner" },
  toaster:      { title: "Toaster",          tagline: "customer support (public)",            price_yr: 0,   route: null },
  oven:         { title: "Oven",             tagline: "master build assistant",               price_yr: 0,   route: null },
  warden:       { title: "Warden",           tagline: "grader + hallucination watchdog",      price_yr: 0,   route: null },
  purser:       { title: "Purser",           tagline: "the payments processor (in build)",    price_yr: 0,   route: null },
};

const HOUSE_ROWS = [
  { slug: "brotherhood",  title: "Brotherhood of Misfits", tagline: "the hiring funnel of the vulnerable", price_yr: 0, route: "/brotherhood" },
  { slug: "rice",         title: "Rice the World",          tagline: "standalone grocery — wholesale + 20%", price_yr: 0, route: "/rice-the-world" },
  { slug: "hardship",     title: "Hardship Door",           tagline: "apply for paid work or a discount",   price_yr: 0, route: "/hardship" },
  { slug: "wallet",       title: "Your Wallet",             tagline: "rolling 30-day cash-out window",      price_yr: 0, route: "/wallet" },
  { slug: "bundles",      title: "Bundle Picker",           tagline: "who-am-I → here's-your-bundle",       price_yr: 0, route: "/bundles" },
  { slug: "tiny_homes",   title: "Tiny Homes Project",      tagline: "per-county homelessness collapse",    price_yr: 0, route: "/tiny-homes" },
  { slug: "ledger",       title: "Public Ledger",           tagline: "every payment, every row",            price_yr: 0, route: "/sage-ledger" },
  { slug: "philanthropy", title: "The Overflow",            tagline: "where the coins go",                  price_yr: 0, route: "/where-the-coins-go" },
  { slug: "tiers",        title: "Tiers",                   tagline: "flat pricing, one page",              price_yr: 0, route: "/tiers" },
  { slug: "investment",   title: "Investment Room",         tagline: "the one planned round",               price_yr: 0, route: "/investor-notes" },
];

export default function AgentsPage() {
  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("loading");

  useEffect(() => {
    fetch(`${API}/api/sage/agents/all`)
      .then((r) => {
        if (!r.ok) throw new Error(`server said ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const live = (data.agents || []).map((a) => {
          const local = ROUTE_MAP[a.slug] || {};
          return {
            slug: a.slug,
            title: local.title || a.slug,
            tagline: local.tagline || a.one_liner || a.trade_role || "",
            price_yr: typeof local.price_yr === "number" ? local.price_yr : null,
            route: local.route || null,
            status: a.status || "in_production",
          };
        });
        // Append the house rows (community/philanthropy doors, not in the roster).
        setAgents([...live, ...HOUSE_ROWS.map((h) => ({ ...h, status: "in_production" }))]);
        setSource("live");
        setLoading(false);
      })
      .catch(() => {
        // Fallback: use only the ROUTE_MAP known-good slugs + the house rows.
        const fallbackList = Object.entries(ROUTE_MAP).map(([slug, v]) => ({
          slug, ...v, status: "unknown",
        }));
        setAgents([...fallbackList, ...HOUSE_ROWS.map((h) => ({ ...h, status: "unknown" }))]);
        setSource("fallback");
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="agents-page">
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">The Baker and Sage family</span>
        </div>
        <p className="mt-6 text-lg font-serif text-[#4a5a48] max-w-3xl">
          Every agent, every house, every open door — on one page. Walk through any card that has a path.
          Paid agents are one or two hundred dollars a year (a few specialist agents at one or two thousand);
          public-service tools and community doors are free.
        </p>
        {source === "fallback" && (
          <p className="mt-3 text-sm font-serif italic text-[#b58c2a]" data-testid="agents-source-note">
            Live roster door is quiet right now; showing the local map. Refresh to try live again.
          </p>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="agents-grid">
        {loading && (
          <div className="text-[#7c8a75] font-serif italic" data-testid="agents-loading">
            Fetching the roster…
          </div>
        )}
        {!loading && agents && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agents
              .slice()
              .sort((a, b) => {
                // Founder ruling 2026-08-12: all free first, then cheapest paid first, then tiered up.
                // Null / unknown prices land at the end.
                const pa = a.price_yr === null || a.price_yr === undefined ? Number.POSITIVE_INFINITY : a.price_yr;
                const pb = b.price_yr === null || b.price_yr === undefined ? Number.POSITIVE_INFINITY : b.price_yr;
                if (pa !== pb) return pa - pb;
                return (a.title || "").localeCompare(b.title || "");
              })
              .map((a) => {
              const clickable = !!a.route;
              const inner = (
                <>
                  <div className="text-lg font-serif font-bold text-[#2c3e2a]">{a.title}</div>
                  <div className="text-sm font-serif italic text-[#7c8a75] mt-1">{a.tagline}</div>
                  <div className="mt-4 text-sm font-serif">
                    {a.price_yr > 0 ? (
                      <span className="text-[#2c3e2a] font-bold">${a.price_yr}/yr</span>
                    ) : a.price_yr === 0 ? (
                      <span className="text-[#b58c2a] italic">free · public</span>
                    ) : (
                      <span className="text-[#7c8a75] italic">price varies</span>
                    )}
                  </div>
                  {clickable && (
                    <div className="mt-3 text-xs font-serif text-[#b58c2a] uppercase tracking-wide">Walk through →</div>
                  )}
                </>
              );
              return clickable ? (
                <a
                  key={a.slug}
                  href={a.route}
                  className="bg-white border border-[#d9d3bd] rounded p-5 hover:border-[#2c3e2a] hover:shadow transition block"
                  data-testid={`agent-card-${a.slug}`}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={a.slug}
                  className="bg-white border border-[#d9d3bd] rounded p-5"
                  data-testid={`agent-card-${a.slug}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          The whole family on the shelf today. More on the way. Same fire · different notes.
        </p>
      </footer>
    </div>
  );
}
